/*
 * Error-classification helpers for the global error handlers in `App.vue`.
 */

/**
 * URL schemes of scripts injected by browser extensions. Extensions run
 * "inpage"/content scripts in the page's own context, so their uncaught
 * errors and unhandled rejections surface in our global handlers like ours
 * do.
 */
const EXTENSION_SCHEMES = new Set([
  "chrome-extension",
  "moz-extension",
  "safari-extension",
  "safari-web-extension",
]);

/** The URL scheme of one stack-frame line, if it carries a source location. */
const frameScheme = (line: string): string | undefined =>
  /([a-z][a-z0-9+.-]*):\/\//i.exec(line)?.[1].toLowerCase();

/**
 * Whether `error` originates in a browser extension: every stack frame with
 * a source location points at an extension URL (e.g. a crypto-wallet's
 * injected `inpage.js` rejecting with "func sseError not found",
 * FRONTEND-FHE). Such failures are not app errors: they must neither crash
 * the app nor be reported.
 *
 * Conservative by construction: a stack mixing extension frames with app (or
 * any non-extension) frames, or carrying no source locations at all, is not
 * recognized — erring toward keeping the report.
 */
export const isExtensionError = (error: unknown): boolean => {
  if (typeof error !== "object" || error === null) return false;
  if (!("stack" in error) || typeof error.stack !== "string") return false;
  const schemes = error.stack
    .split("\n")
    // Frame lines only ("    at fn (url:1:2)" on V8, "fn@url:1:2" on
    // Firefox/Safari): the message lines of the stack may quote URLs.
    .filter((line) => /^\s*at\s|@/.test(line))
    .map(frameScheme)
    .filter((scheme) => scheme !== undefined);
  return (
    schemes.length > 0 &&
    schemes.every((scheme) => EXTENSION_SCHEMES.has(scheme))
  );
};
